
// ------------------- Hook for all stock related endpoint calls --------------------
// ----------------------------------------------------------------------------------
// load in the use functions and the api service functions
import { useState, useEffect, useCallback } from 'react';
import { getOHLVCData, getLatestPrice } from '../services/api';


// hook for collecting the latest price of a stock symbol
export const useLatestPrice = (symbol, refreshInterval = 60000) => {

  // set ethe state variables for the price, loading and error states
  const [latestPrice, setLatestPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // function to fetch the latest price of a stock symbol
  const fetchPrice = useCallback(async () => {
    if (!symbol) return; // if there is no symbol, return
    
    try {

      setError(null); // set the error state to null
      const data = await getLatestPrice(symbol); // call the api service function to get the latest price of the stock symbol
      setLatestPrice(data); // set the latest price state with the response data

    } catch (err) { // catch any errors and set the error state
      console.error('Error fetching latest price:', err);
      setError(err.message);

    } finally {
      setLoading(false);
    }
  }, [symbol]); // use callback to remember the function and only recreate it if the symbol changes

  useEffect(() => {
    fetchPrice(); // call the fetchPrice function when the component mounts or when the symbol changes
    
    if (refreshInterval > 0) { // if the refresh interval is greater than 0, set an interval to fetch the latest price X time
      const interval = setInterval(fetchPrice, refreshInterval);
      return () => clearInterval(interval);

    }
  }, [fetchPrice, refreshInterval]);

  return { latestPrice, loading, error, refetch: fetchPrice };
};


// hook for collecting the stock OHLVC data
export const useChartData = (symbol, timeframe, compareSymbols = []) => {

  // set the state variables for the chart data, available columns, loading and error states
  const [chartData, setChartData] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // function to fetch the stock OHLVC data
  const fetchData = useCallback(async () => {
    if (!symbol) return; // if there is no symbol, return blank

    try {
      // set the loading and error states
      setLoading(true);
      setError(null);

      // call the api service function to get the stock OHLVC data
      const compareParam = compareSymbols.length > 0 ? compareSymbols.join(',') : null; // if there are compare symbols, join them into a string
      const response = await getOHLVCData({ symbol, timeframe, compare: compareParam });

      // check if the response data is empty and throw an error if it is
      if (!response?.data || response.data.length === 0) {
        throw new Error(`No data available for ${symbol}`);
      }

      // build a date indexed map with
      const primaryMap = {};
      response.data.forEach(row => {
        primaryMap[row.date] = { time: row.date, ...row };
      });

      // merge the compare data into the primary map
      if (response.compare) {

        Object.entries(response.compare).forEach(([sym, rows]) => { // convert the object into an array of [key, value] pairs and loop through them
          rows.forEach(row => {
            if (!primaryMap[row.date]) primaryMap[row.date] = { time: row.date }; // check if the date exists in the primary map, if not create it
            
            // loop through the columns and add them to the map with symbol as key prefix
            Object.keys(row).forEach(col => {
              if (col !== 'date') primaryMap[row.date][`${sym}_${col}`] = row[col];
            });
          });
        });
      }

      // sort the merged data by date and convert it into an array - sorted by time in ascending order
      const merged = Object.values(primaryMap).sort((a, b) => a.time.localeCompare(b.time));

      // remove any columns that are not needed for the chart and set the available columns state
      const EXCLUDED = ['index', 'target_action', 'target_return', 'time', 'date'];
      const cols = response.columns 
        ? response.columns.filter(c => !EXCLUDED.includes(c.toLowerCase())) // if the response has columns, filter them to exclude any that are in the EXCLUDED list
        : Object.keys(merged[0] || {}).filter(k => !EXCLUDED.includes(k) && typeof merged[0][k] === 'number');

      setAvailableColumns(cols);
      setChartData(merged);

    } catch (err) { // check for any errors and set the error state
      console.error('Error fetching chart data:', err);
      setError(err.message || 'Failed to fetch chart data');
      setChartData([]);
      setAvailableColumns([]);

    } finally {
      setLoading(false);
    }
  }, [symbol, timeframe, compareSymbols]);

  useEffect(() => {
    fetchData(); // call the fetch function when the component mounts or when the symbol, timeframe or compareSymbols change
  }, [fetchData]);

  return { chartData, availableColumns, loading, error, refetch: fetchData };
};
